// src/components/SidebarThemeToggle.js
//
// UI revamp (UI_REVAMP_ENABLED): the light/dark switch inside the Dashboard,
// Coordinator and Reception sidebars, replacing the floating pill on those pages.
// It doesn't own the mode: it asks LightModeToggle (always mounted, see App.js)
// to flip it, and reads the current mode from the `light-bg-mode` class on <html>.
// The sun/moon icons are drawn here (no icon library, nothing downloaded).

import { useEffect, useState } from "react";
import { TOGGLE_LIGHT_MODE_EVENT } from "./LightModeToggle";

const isLight = () =>
  typeof document !== "undefined" && document.documentElement.classList.contains("light-bg-mode");

export const SunGlyph = ({ className = "" }) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" className={className} aria-hidden="true">
    <circle cx="10" cy="10" r="3.4" />
    <path d="M10 2.2v1.9M10 15.9v1.9M2.2 10h1.9M15.9 10h1.9M4.5 4.5l1.35 1.35M14.15 14.15l1.35 1.35M4.5 15.5l1.35-1.35M14.15 5.85l1.35-1.35" />
  </svg>
);

export const MoonGlyph = ({ className = "" }) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M15.8 12.6A6.6 6.6 0 0 1 7.4 4.2a6.6 6.6 0 1 0 8.4 8.4Z" />
  </svg>
);

export default function SidebarThemeToggle({ expanded = true }) {
  const [light, setLight] = useState(isLight);

  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setLight(isLight()));
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // No visible words (user, 2026-10-30): the icon and the switch say it. The
  // accessible name stays "Dark mode" for screen readers.
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!light}
      aria-label="Dark mode"
      title={light ? "Switch to dark mode" : "Switch to light mode"}
      onClick={() => window.dispatchEvent(new Event(TOGGLE_LIGHT_MODE_EVENT))}
      className={`rx-theme-toggle w-full flex items-center rounded-lg ${
        expanded ? "gap-2 px-2 py-1.5 justify-center" : "p-1.5 justify-center"
      }`}
    >
      {expanded ? (
        // Sun · switch · moon. The knob sits on the side of the current mode,
        // and that side's icon is lit.
        <>
          <SunGlyph className={`w-4 h-4 shrink-0 rx-theme-sun ${light ? "rx-theme-glyph--on" : ""}`} />
          <span className={`rx-theme-toggle-track ${light ? "" : "rx-theme-toggle-track--on"}`} aria-hidden="true">
            <span className="rx-theme-toggle-knob" />
          </span>
          <MoonGlyph className={`w-4 h-4 shrink-0 rx-theme-moon ${light ? "" : "rx-theme-glyph--on"}`} />
        </>
      ) : (
        // Collapsed sidebar: just the current mode's icon.
        <span className="flex items-center justify-center w-7 h-7 shrink-0">
          {light ? (
            <SunGlyph className="w-4 h-4 rx-theme-sun rx-theme-glyph--on" />
          ) : (
            <MoonGlyph className="w-4 h-4 rx-theme-moon rx-theme-glyph--on" />
          )}
        </span>
      )}
    </button>
  );
}
